import { CacheHelper } from 'utils/CacheHelper';
import ReconnectOptions from './ReconnectOptions';
import RMHub from './RMHub';

type HubName = 'notificationHub' | 'systemtaskhub' | 'progresshub'; //more hubs to be added here.
const DEFAULT_OPTIONS: ReconnectOptions = {
  maxRetries: 10,
  baseDelayMs: 2000,
  maxDelayMs: 30000,
  automaticReconnect: false,
};
class HubConnectionManager {
  private static instance: HubConnectionManager;
  private readonly connections: Map<HubName, RMHub> = new Map();
  private readonly pendingConnections: Map<HubName, Promise<RMHub>> = new Map();

  // because we are making it Singleton instance
  private constructor() {
    this.registerTabCloseHandlers();
  }

  public static getInstance(): HubConnectionManager {
    if (!HubConnectionManager.instance) {
      HubConnectionManager.instance = new HubConnectionManager();
    }
    return HubConnectionManager.instance;
  }
  public async closeConnection(hubName: HubName) {
    const connection = this.connections.get(hubName);
    if (connection) {
      try {
        connection.manualStop = true;
        await connection.stop();
      } catch (e) {
        console.error(`Error stopping connection for ${hubName}:`, e);
      }
      this.connections.delete(hubName);
    } else {
      console.warn(`No connection found for hub: ${hubName}`);
    }
  }
  public async getConnection(
    hubName: HubName,
    url: string,
    options?: ReconnectOptions,
  ): Promise<RMHub> {
    if (this.connections.has(hubName)) {
      return this.connections.get(hubName)!;
    }

    if (this.pendingConnections.has(hubName)) {
      return this.pendingConnections.get(hubName)!;
    }

    const loadingPromise = this.getConnectionInternal(hubName, url, options);
    this.pendingConnections.set(hubName, loadingPromise);

    try {
      const hub = await loadingPromise;
      return hub;
    } finally {
      this.pendingConnections.delete(hubName);
    }
  }
  // Main method to get/create connections
  private async getConnectionInternal(
    hubName: HubName,
    url: string,
    options?: ReconnectOptions,
  ): Promise<RMHub> {
    if (this.connections.has(hubName)) {
      return this.connections.get(hubName);
    }
    const token = await CacheHelper.get('token');
    //include auth token, after making socket routes protected
    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

    try {
      const hub = new RMHub(hubName, url, token, DEFAULT_OPTIONS);
      await hub.setupConnection([], mergedOptions);
      this.connections.set(hubName, hub);
      return hub;
    } catch (error) {
      console.error(`Failed to initialize ${hubName}:`, error);
      throw error;
    }
  }

  private registerTabCloseHandlers() {
    // Modern browsers (including mobile)
    window.addEventListener('pagehide', this.cleanupAllConnections);

    // Legacy browser support
    window.addEventListener('beforeunload', this.cleanupAllConnections);
  }

  private cleanupAllConnections = () => {
    this.connections.forEach((connection) => {
      try {
        connection.stop().catch((e) => console.error('Stop failed:', e));
      } catch (e) {
        console.error('Cleanup error:', e);
      }
    });
    this.connections.clear();
  };
}

export default HubConnectionManager.getInstance();
